import type { FilterConfig, FilterOption } from './Filter.types';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useResponsive } from '../hooks/useResponsive';
import FilterModal from './FilterModal';
import { log } from '../utils/log';

// Local types specific to Filters component

export interface SortOption {
  id: string;
  label: string;
}

export interface FiltersProps {
  // Filter configuration
  filters?: FilterConfig[];
  onFilterChange?: (filterId: string, value: string | string[]) => void;

  // Sort configuration
  sortOptions?: SortOption[];
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  onSortChange?: (orderBy: string, orderDirection: 'ASC' | 'DESC') => void;
  sortSize?: 'small' | 'medium'; // Size of the sort dropdown

  // Clear all filters
  onClearAllFilters?: () => void;

  // Local storage keys for persistence
  storagePrefix?: string;

  // Current filter state exposed to consumers
  currentFilters?: Record<string, string | string[]>;

  // Width configuration
  defaultWidth?: string; // Default width for all filters (e.g., '250px', '100%')

  // Modal mode configuration
  modalMode?: boolean; // If true, show filter icon instead of inline filters
  showLabel?: boolean; // Whether to show "Filters:" label

  // localStorage loading control
  disableAutoLoad?: boolean; // If true, skip automatic localStorage loading (parent handles it)
}

const Filters: React.FC<FiltersProps> = ({
  filters = [],
  onFilterChange,
  sortOptions = [],
  orderBy = 'createdAt',
  orderDirection = 'DESC',
  onSortChange,
  sortSize = 'small',
  onClearAllFilters,
  storagePrefix = 'filters',
  currentFilters = {},
  defaultWidth = '200px',
  modalMode = false,
  showLabel = true,
  disableAutoLoad = false,
}) => {
  const [sortKey, setSortKey] = useState(orderBy);
  const [sortAsc, setSortAsc] = useState(orderDirection === 'ASC');
  const { isMobile } = useResponsive();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);

  // State management for debounced textfields
  const [textfieldValues, setTextfieldValues] = useState<Record<string, string>>({});

  // Use refs to store latest values without causing re-renders
  const onFilterChangeRef = useRef(onFilterChange);
  const onSortChangeRef = useRef(onSortChange);
  const filtersRef = useRef(filters);

  // Update refs when props change
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
    onSortChangeRef.current = onSortChange;
    filtersRef.current = filters;
  }, [onFilterChange, onSortChange, filters]);

  // Initialize local state from props and localStorage
  useEffect(() => {
    const savedOrderBy = localStorage.getItem(`${storagePrefix}_orderBy`);
    const savedDirection = localStorage.getItem(`${storagePrefix}_direction`);

    // Determine the actual values to use
    const finalOrderBy = savedOrderBy || orderBy;
    const finalDirection = savedDirection ? (savedDirection === 'ASC' ? 'ASC' : 'DESC') : orderDirection;

    if (savedOrderBy) {
      setSortKey(savedOrderBy);
    }
    if (savedDirection) {
      setSortAsc(savedDirection === 'ASC');
    }

    // Notify parent of saved sort values (or initial values if no saved state)
    if (onSortChangeRef.current && (savedOrderBy || savedDirection)) {
      onSortChangeRef.current(finalOrderBy, finalDirection);
    }

    // Load saved filters and notify parent component (only if not disabled)
    if (!disableAutoLoad) {
      const savedFilters = localStorage.getItem(`${storagePrefix}_filters`);
      if (savedFilters && onFilterChangeRef.current) {
        try {
          const parsedFilters = JSON.parse(savedFilters);
          // Apply each saved filter
          Object.entries(parsedFilters).forEach(([filterId, value]) => {
            onFilterChangeRef.current!(filterId, value as string | string[]);
          });
        } catch (error) {
          log.error('Error parsing saved filters:', error);
        }
      }
    }

    // Apply default values for toggle filters when no saved values exist (only if not disabled)
    if (!disableAutoLoad && onFilterChangeRef.current) {
      const savedFilters = localStorage.getItem(`${storagePrefix}_filters`);
      let savedFiltersParsed: Record<string, string | string[]> = {};

      if (savedFilters) {
        try {
          savedFiltersParsed = JSON.parse(savedFilters);
        } catch (error) {
          log.error('Error parsing saved filters:', error);
        }
      }

      filtersRef.current.forEach((filter) => {
        if (filter.type === 'toggle' && !savedFiltersParsed[filter.id]) {
          const defaultOptions = filter.options?.filter((option) => option.default);

          if (defaultOptions && defaultOptions.length > 0) {
            if (filter.exclusive) {
              // For exclusive toggles, use the first default option
              onFilterChangeRef.current!(filter.id, defaultOptions[0].value);
            } else {
              // For non-exclusive toggles, use all default options
              const defaultValues = defaultOptions.map((option) => option.value);
              if (defaultValues.length > 0) {
                onFilterChangeRef.current!(filter.id, defaultValues);
              }
            }
          }
        }
      });
    }
  }, [disableAutoLoad, storagePrefix, orderBy, orderDirection]);

  // Handle debounced textfield values (only for single textfields)
  useEffect(() => {
    const singleTextfieldFilters = filters.filter((f) => f.type === 'textfield' && !f.multiple);
    const timeouts: NodeJS.Timeout[] = [];

    singleTextfieldFilters.forEach((filter) => {
      const delay = filter.debounceMillis ?? 300;
      const currentValue = textfieldValues[filter.id];

      if (currentValue !== undefined && currentValue !== (currentFilters[filter.id] || '')) {
        const timeout = setTimeout(() => {
          if (onFilterChange) {
            onFilterChange(filter.id, currentValue);
          }
        }, delay);
        timeouts.push(timeout);
      }
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [textfieldValues, onFilterChange, currentFilters, filters]);

  // Handle sort changes
  const handleSortChange = (key: string) => {
    setSortKey(key);
    localStorage.setItem(`${storagePrefix}_orderBy`, key);
    if (onSortChange) {
      onSortChange(key, sortAsc ? 'ASC' : 'DESC');
    }
  };

  const toggleDirection = () => {
    const newDirection = !sortAsc;
    setSortAsc(newDirection);
    const directionString = newDirection ? 'ASC' : 'DESC';
    localStorage.setItem(`${storagePrefix}_direction`, directionString);
    if (onSortChange) {
      onSortChange(sortKey, directionString);
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    if (onFilterChange) {
      onFilterChange(filterId, value);
    }

    // Save individual filter to localStorage
    const savedFilters = localStorage.getItem(`${storagePrefix}_filters`);
    let filters: Record<string, string | string[]> = {};

    if (savedFilters) {
      try {
        filters = JSON.parse(savedFilters);
      } catch (error) {
        log.error('Error parsing saved filters:', error);
      }
    }

    // Update the specific filter
    filters[filterId] = value;

    // Save back to localStorage
    localStorage.setItem(`${storagePrefix}_filters`, JSON.stringify(filters));
  };

  // Count active filters (for modal mode badge) - only count filters that go in modal
  const countActiveFilters = () => {
    const filtersToCount = modalMode ? filters.filter((f) => f.modalMode !== false) : filters;

    return Object.entries(currentFilters).reduce((count, [filterId, value]) => {
      const filterConfig = filtersToCount.find((f) => f.id === filterId);
      if (!filterConfig) {
        return count;
      }

      // Don't count exclusive toggles as they always have a value
      if (filterConfig.type === 'toggle' && filterConfig.exclusive) {
        return count;
      }

      // Count if this filter has an active value
      if (Array.isArray(value)) {
        return count + (value.length > 0 ? 1 : 0);
      }
      return count + (value !== '' && value !== undefined && value !== null ? 1 : 0);
    }, 0);
  };

  // Check if any clearable filters are applied (exclude exclusive toggles)
  const hasActiveClearableFilters = () => {
    return Object.entries(currentFilters).some(([filterId, value]) => {
      // Find the filter config for this filter ID
      const filterConfig = filters.find((f) => f.id === filterId);

      // Skip filters that don't have a corresponding configuration
      if (!filterConfig) {
        return false;
      }

      // Skip exclusive toggles as they are not clearable
      if (filterConfig?.type === 'toggle' && filterConfig?.exclusive) {
        return false;
      }

      // Check if this filter has an active value
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== undefined && value !== null;
    });
  };

  // Check if there are any clearable filters in the configuration
  const hasClearableFilters = () => {
    return filters.some((filter) => {
      // Exclusive toggles are not clearable
      if (filter.type === 'toggle' && filter.exclusive) {
        return false;
      }
      return true;
    });
  };

  // Handle clear all filters - only clear non-exclusive toggle filters
  const handleClearAllFilters = () => {
    if (onClearAllFilters) {
      onClearAllFilters();
    }

    // Clear textfield values that are managed locally with debouncing
    const clearedTextfieldValues: Record<string, string> = {};
    filters.forEach((filter) => {
      if (filter.type === 'textfield') {
        clearedTextfieldValues[filter.id] = '';
      }
    });
    setTextfieldValues(clearedTextfieldValues);

    // Clear saved filters from localStorage, but preserve exclusive toggles
    const savedFilters = localStorage.getItem(`${storagePrefix}_filters`);
    const preservedFilters: Record<string, string | string[]> = {};

    if (savedFilters) {
      try {
        const currentSavedFilters = JSON.parse(savedFilters);

        // Preserve exclusive toggle values
        filters.forEach((filter) => {
          if (filter.type === 'toggle' && filter.exclusive && currentSavedFilters[filter.id]) {
            preservedFilters[filter.id] = currentSavedFilters[filter.id];
          }
        });
      } catch (error) {
        log.error('Error parsing saved filters:', error);
      }
    }

    // Save back only the preserved filters
    if (Object.keys(preservedFilters).length > 0) {
      localStorage.setItem(`${storagePrefix}_filters`, JSON.stringify(preservedFilters));
    } else {
      localStorage.removeItem(`${storagePrefix}_filters`);
    }
  };

  // Helper function to wrap components with tooltip if provided
  const withTooltip = (component: React.ReactNode, tooltip?: string) => {
    if (tooltip) {
      return (
        <Tooltip title={tooltip} arrow placement="top">
          <div style={{ display: 'inline-block', width: '100%' }}>{component}</div>
        </Tooltip>
      );
    }
    return component;
  };

  // Render individual filter based on type
  const renderFilter = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'textfield': {
        if (filter.multiple) {
          // Handle multiple textfield with chips using Autocomplete
          const currentValue = (currentFilters[filter.id] as string[]) || [];

          return withTooltip(
            <FormControl
              key={filter.id}
              className={clsx('filters-form-control', { 'filters-form-control--mobile': isMobile })}
            >
              <Autocomplete
                multiple
                freeSolo
                options={[]} // No predefined options for free text entry
                value={currentValue}
                onChange={(_event, newValue) => {
                  // Filter out empty strings and ensure uniqueness
                  const filteredValues = newValue.filter(
                    (val, index, arr) => typeof val === 'string' && val.trim() !== '' && arr.indexOf(val) === index,
                  ) as string[];
                  handleFilterChange(filter.id, filteredValues);
                }}
                onInputChange={(_event, newInputValue, reason) => {
                  // Handle comma-separated input by splitting and adding multiple values
                  if (reason === 'input' && newInputValue.includes(',')) {
                    const newValues = newInputValue
                      .split(',')
                      .map((val) => val.trim())
                      .filter((val) => val !== '' && !currentValue.includes(val));

                    if (newValues.length > 0) {
                      const updatedValues = [...currentValue, ...newValues];
                      handleFilterChange(filter.id, updatedValues);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={filter.label}
                    placeholder={currentValue.length > 0 ? 'Add another...' : filter.placeholder}
                    size={filter.size || 'small'}
                    data-testid={filter.testId}
                    InputProps={params.InputProps as any}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} className="select-chip" />
                  ))
                }
                size={filter.size || 'small'}
                fullWidth
              />
            </FormControl>,
            filter.tooltip,
          );
        }

        // Handle single textfield
        const currentValue = textfieldValues[filter.id] ?? currentFilters[filter.id] ?? '';

        return withTooltip(
          <FormControl
            key={filter.id}
            className={clsx('filters-form-control', { 'filters-form-control--mobile': isMobile })}
            sx={{ width: filter.width || defaultWidth }}
          >
            <TextField
              label={filter.label}
              placeholder={filter.placeholder}
              size={filter.size || 'small'}
              value={currentValue}
              onChange={(e) => {
                const value = e.target.value;
                setTextfieldValues((prev) => ({
                  ...prev,
                  [filter.id]: value,
                }));
              }}
              data-testid={filter.testId}
              fullWidth
            />
          </FormControl>,
          filter.tooltip,
        );
      }

      case 'select': {
        const currentValue = currentFilters[filter.id] || (filter.multiple ? [] : '');

        if (filter.autocomplete) {
          // Use Autocomplete for better UX with grouping and search
          const selectedOptions = filter.multiple
            ? ((currentValue as string[])
                .map((val) => filter.options?.find((opt) => opt.value === val))
                .filter(Boolean) as FilterOption[])
            : filter.options?.find((opt) => opt.value === currentValue) || null;

          return withTooltip(
            <FormControl
              key={filter.id}
              className={clsx('filters-form-control', { 'filters-form-control--mobile': isMobile })}
            >
              <Autocomplete<FilterOption, boolean, true, false>
                disableClearable
                options={filter.options || []}
                value={
                  filter.multiple
                    ? (selectedOptions as FilterOption[])
                    : (selectedOptions as FilterOption | null) || undefined
                }
                onChange={(_event, newValue) => {
                  if (filter.multiple && Array.isArray(newValue)) {
                    const values = newValue.map((option) => option.value);
                    handleFilterChange(filter.id, values);
                  } else if (!filter.multiple && newValue && !Array.isArray(newValue)) {
                    handleFilterChange(filter.id, newValue.value);
                  }
                }}
                getOptionLabel={(option: FilterOption) => option.name}
                isOptionEqualToValue={(option: FilterOption, value: FilterOption) => option.value === value.value}
                groupBy={(option: FilterOption) => option.group || ''}
                renderGroup={(params) =>
                  params.group ? (
                    <li key={params.key}>
                      <Typography
                        variant="subtitle2"
                        sx={{ backgroundColor: '#f0f0f0', padding: '4px 8px', fontWeight: 600 }}
                      >
                        {params.group}
                      </Typography>
                      <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
                    </li>
                  ) : (
                    <ul key={params.key} style={{ margin: 0, padding: 0 }}>
                      {params.children}
                    </ul>
                  )
                }
                renderOption={(props, option: FilterOption) => (
                  <Box component="li" {...props} key={option.value}>
                    <Typography variant="body1">{option.name}</Typography>
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.name}
                      size="small"
                      className="select-chip"
                      onDelete={() => {
                        const newValues = (currentValue as string[]).filter((val) => val !== option.value);
                        handleFilterChange(filter.id, newValues);
                      }}
                      sx={{
                        height: '30px',
                        fontSize: '0.875rem',
                        m: 0.5,
                        borderRadius: '6px',
                        backgroundColor: 'rgba(59, 111, 217, 0.08)',
                        border: '1px solid rgba(59, 111, 217, 0.2)',
                        '& .MuiChip-label': {
                          padding: '0 10px',
                          fontWeight: 500,
                          color: '#3b6fd9',
                        },
                        '& .MuiChip-deleteIcon': {
                          marginLeft: '6px',
                          marginRight: '-2px',
                          fontSize: '18px',
                          color: 'rgba(59, 111, 217, 0.6)',
                          transition: 'color 0.2s',
                          '&:hover': {
                            color: '#3b6fd9',
                          },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={filter.label}
                    placeholder={filter.placeholder}
                    size={filter.size || 'small'}
                    data-testid={filter.testId}
                    fullWidth
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      style: {
                        fontWeight: 400,
                        fontSize: '1rem',
                      },
                    }}
                    InputProps={params.InputProps as any}
                  />
                )}
                multiple={filter.multiple}
                size={filter.size || 'small'}
                fullWidth
              />
            </FormControl>,
            filter.tooltip,
          );
        }

        // Fallback to regular Select for backward compatibility
        return withTooltip(
          <FormControl
            key={filter.id}
            className={clsx('filters-form-control', { 'filters-form-control--mobile': isMobile })}
            sx={{ width: filter.width || defaultWidth }}
          >
            <InputLabel id={`${filter.id}-label`} size={filter.size === 'medium' ? 'medium' : 'small'}>
              {filter.label}
            </InputLabel>
            <Select
              className="filters-select"
              labelId={`${filter.id}-label`}
              label={filter.label}
              name={filter.id}
              data-testid={filter.testId}
              size={filter.size || 'small'}
              value={currentValue}
              onChange={(event) => {
                const value = event.target.value;
                handleFilterChange(filter.id, value);
              }}
              multiple={filter.multiple}
              fullWidth
              MenuProps={{ classes: { paper: 'filters-menu-paper' } }}
              input={filter.multiple ? <OutlinedInput label={filter.label} /> : undefined}
              renderValue={
                filter.multiple
                  ? (selected) => (
                      <Box className="select-chips">
                        {(selected as string[]).map((value) => {
                          const option = filter.options?.find((opt) => opt.value === value);
                          return (
                            <Chip
                              key={value}
                              label={option?.name || value}
                              className="select-chip"
                              onDelete={() => {
                                const newValues = (selected as string[]).filter((val) => val !== value);
                                handleFilterChange(filter.id, newValues);
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          );
                        })}
                      </Box>
                    )
                  : undefined
              }
            >
              {filter.clearable && (
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
              )}
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {filter.multiple ? (
                    <>
                      <input
                        type="checkbox"
                        checked={((currentFilters[filter.id] as string[]) || []).includes(option.value)}
                        readOnly
                        style={{ marginRight: 8 }}
                      />
                      <ListItemText primary={option.name} />
                    </>
                  ) : (
                    option.name
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>,
          filter.tooltip,
        );
      }

      case 'toggle': {
        const toggleValue = currentFilters[filter.id];
        const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | string[] | null) => {
          if (filter.exclusive) {
            // For exclusive toggles, prevent clearing - always ensure a value is set
            if (newValue !== null && newValue !== '') {
              handleFilterChange(filter.id, newValue);
            }
            // If newValue is null/empty, keep the current value (don't clear)
          } else {
            // For non-exclusive toggles, newValue will be an array
            handleFilterChange(filter.id, newValue || []);
          }
        };

        return (
          <div
            key={filter.id}
            className={clsx('filters-toggle-wrapper', { 'filters-toggle-wrapper--mobile': isMobile })}
          >
            {filter.showLabel !== false && (
              <Typography variant="subtitle2" fontWeight={600}>
                {filter.label}:
              </Typography>
            )}
            <ToggleButtonGroup
              className="filters-toggle-group"
              value={toggleValue}
              onChange={handleToggleChange}
              exclusive={filter.exclusive}
              data-testid={filter.testId}
            >
              {(filter.options || []).map((option) => {
                const buttonContent = (
                  <ToggleButton className="filters-toggle-button" key={option.value} value={option.value}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      {option.icon && (
                        <span
                          style={{
                            marginRight: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 1,
                          }}
                        >
                          {option.icon}
                        </span>
                      )}
                      <span style={{ lineHeight: 1 }}>{option.name}</span>
                    </span>
                    {option.count !== undefined && option.count > 0 && (
                      <span className="filters-badge">{option.count}</span>
                    )}
                  </ToggleButton>
                );

                return option.tooltip ? (
                  <Tooltip key={option.value} title={option.tooltip} arrow placement="top">
                    {buttonContent}
                  </Tooltip>
                ) : (
                  buttonContent
                );
              })}
            </ToggleButtonGroup>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Separate filters based on modalMode setting
  const inlineFilters = modalMode ? filters.filter((filter) => filter.modalMode === false) : filters;
  const modalFilters = modalMode ? filters.filter((filter) => filter.modalMode !== false) : [];

  // Reusable sort controls component
  const renderSortControls = () => {
    if (sortOptions.length === 0) {
      return null;
    }

    return (
      <div className={clsx('filters-sort-controls', { 'filters-sort-controls--mobile': isMobile })}>
        <FormControl
          className={clsx('filters-form-control', 'filters-form-control--sort', {
            'filters-form-control--mobile': isMobile,
          })}
        >
          <InputLabel id="sort-by-label">Sort by</InputLabel>
          <Select
            className="filters-sort-select"
            labelId="sort-by-label"
            label="Sort by"
            name="sort-by"
            data-testid="sort-select"
            size={sortSize}
            value={sortKey}
            onChange={(event) => handleSortChange(event.target.value as string)}
            variant="outlined"
            MenuProps={{ classes: { paper: 'filters-menu-paper' } }}
          >
            {sortOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton
          className="filters-sort-direction"
          onClick={toggleDirection}
          title={sortAsc ? 'Ascending' : 'Descending'}
          data-testid="sort-direction-toggle"
        >
          {sortAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </IconButton>
      </div>
    );
  };

  // Inline filters region — renders the input grid only. No external eyebrow
  // label; if the consumer wants one they can render a chip outside the bar.
  const renderInlineFiltersSection = (filtersToRender: FilterConfig[]) => {
    if (filtersToRender.length === 0) {
      return null;
    }

    return (
      <div className={clsx('filters-controls', { 'filters-controls--mobile': isMobile })}>
        {filtersToRender.map((filter) => renderFilter(filter))}
      </div>
    );
  };

  // Reusable clear all button
  const renderClearAllButton = () => {
    if (!hasClearableFilters() || !hasActiveClearableFilters()) {
      return null;
    }

    return (
      <Button
        className="filters-clear-button"
        variant="outlined"
        size="medium"
        startIcon={<ClearIcon />}
        onClick={handleClearAllFilters}
        data-testid="clear-all-filters"
      >
        Clear Filters
      </Button>
    );
  };

  // Reusable modal trigger button
  const renderModalTrigger = (activeFilterCount: number) => {
    return (
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        sx={{
          width: isMobile ? '100%' : 'auto',
          '& .MuiBadge-badge': {
            backgroundColor: activeFilterCount > 0 ? 'var(--color-brand-primary)' : 'transparent',
          },
        }}
      >
        <Button
          onClick={() => setModalOpen(true)}
          size="large"
          sx={{
            color: 'var(--color-brand-primary)',
            borderColor: 'var(--color-brand-primary)',
            borderWidth: '2px',
            borderStyle: 'solid',
            backgroundColor: 'var(--color-grey)',
            '&:hover': {
              backgroundColor: 'var(--color-cornflower-blue)',
            },
            width: isMobile ? '100%' : '180px',
          }}
          title="Open Filters"
          data-testid="filter-modal-trigger"
        >
          <FilterListIcon />
          Add Filters
        </Button>
      </Badge>
    );
  };

  // Flat container: every direct child of `.filters-group` becomes a flex
  // item on the same row. Inner helpers (renderInlineFiltersSection,
  // renderSortControls) own their own wrapper divs — we don't double-wrap.
  const renderFilterBar = (filterContent: React.ReactNode, sortContent: React.ReactNode) => {
    return (
      <div className="filters-bar">
        <div className="filters-group">
          {filterContent}
          {sortContent}
        </div>
      </div>
    );
  };

  // Modal mode: show compact filter icon with badge for modal filters, inline for others
  if (modalMode) {
    const activeFilterCount = countActiveFilters();
    const hasModalFilters = modalFilters.length > 0;

    const filterContent = (
      <>
        {/* Show filter icon only if there are filters that should go in modal */}
        {hasModalFilters && renderModalTrigger(activeFilterCount)}

        {/* Render inline filters */}
        {renderInlineFiltersSection(inlineFilters)}

        {/* Show clear all button if there are active clearable filters */}
        {renderClearAllButton()}
      </>
    );

    const sortContent = renderSortControls();

    return (
      <>
        {renderFilterBar(filterContent, sortContent)}

        {/* Only show modal if there are filters that should go in it */}
        {hasModalFilters && (
          <FilterModal
            filters={modalFilters}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            currentFilters={currentFilters}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}
      </>
    );
  }

  // Default inline mode
  const filterContent = (
    <>
      {renderInlineFiltersSection(filters)}
      {renderClearAllButton()}
    </>
  );

  const sortContent = renderSortControls();

  return renderFilterBar(filterContent, sortContent);
};

export default Filters;
